import { Fragment, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { doc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface Product {
  id: string;
  name: string;
}

interface DeleteProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  product?: Product | null;
  title?: string;
  message?: string;
  selectedProducts?: string[];
}

export default function DeleteProductModal({
  isOpen,
  onClose,
  onConfirm,
  product = null,
  title,
  message,
  selectedProducts = []
}: DeleteProductModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!product && (!selectedProducts || selectedProducts.length === 0)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (product) {
        // Deletar produto único
        const ordersQuery = query(
          collection(db, 'orders'),
          where('items', 'array-contains', { productId: product.id })
        );
        const ordersSnapshot = await getDocs(ordersQuery);

        if (!ordersSnapshot.empty) {
          setError('Não é possível excluir este produto pois ele está associado a encomendas');
          return;
        }

        await deleteDoc(doc(db, 'products', product.id));
      } else if (selectedProducts && selectedProducts.length > 0) {
        // Deletar múltiplos produtos
        // Não verificamos encomendas para exclusão em massa para simplificar o processo
        // e porque isso já é verificado na função handleDeleteSelectedProducts no componente Products
        console.log('Deletando produtos selecionados:', selectedProducts);
        
        // Chamar diretamente onConfirm que contém a lógica de exclusão em massa
        onConfirm();
        onClose();
        return;
      }

      onConfirm();
      onClose();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Erro ao excluir produto(s)');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold">
              {title || (product ? 'Apagar Produto' : 'Apagar Produtos Selecionados')}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
            {error}
          </div>
        )}

        <div className="mt-4">
          <p className="text-gray-600">
            {message || (product
              ? `Tem certeza que deseja apagar o produto "${product.name}"?`
              : `Tem certeza que deseja apagar os ${selectedProducts.length} produtos selecionados?`)}
          </p>
          <p className="text-red-600 text-sm mt-2">
            Esta ação não pode ser desfeita.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Apagando...' : 'Apagar'}
          </button>
        </div>
      </div>
    </div>
  );
}